import { zodResolver } from "@hookform/resolvers/zod";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import {
    Button,
    Checkbox,
    Divider,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/pl";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { z } from "zod";
import BasicDialog from "../../components/base/BasicDialog";
import { SnackbarError } from "../../components/base/SnackbarAlert/SnackbarError";
import { SearchOtherClientDialog } from "../../components/dialog/ClientDiscoveryDialog/SearchOtherClientDialog";
import { AllDayTodoSwitches } from "../../components/form/AllDayTodoSwitches";
import { ControlledSelect } from "../../components/form/ControlledSelect/ControlledSelect";
import { ControlledTextField } from "../../components/form/ControlledTextField/ControlledTextField";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import { useCustomerById } from "../../services/customersService";
import {
    useCreateTaskMutation,
    useGetTaskStatuses,
    useGetTaskSubjects,
    useGetTaskSubsubjects,
} from "../../services/taskService";
import { useGetSellers } from "../../services/usersApi";
import { generateOutlookCalendarLink } from "../../utils";

import { ClientPassportButton } from "../../components/buttons/ClientPassportButton";
import {
    MAX_WORK_TIME,
    MAX_WORK_TIME_DAYJS,
    MINUTES_TO_MILLISECONDS,
    MIN_WORK_TIME,
    MIN_WORK_TIME_DAYJS,
} from "../../helpers/constants";
import useUserId from "../../hooks/useUserId";
import { getDefaultTaskPeriodStr } from "./utils";

export const validateWorkTime = (time) => {
    if (time) {
        const [hours, minutes] = time.split(":").map(Number);
        const totalMinutes = hours * 60 + minutes;
        return totalMinutes >= MIN_WORK_TIME && totalMinutes <= MAX_WORK_TIME;
    }
    return true;
};

const createTaskFormSchema = z
    .object({
        name: z.string().min(1, { message: t("tasks.validation.nameRequired") }),
        way: z.enum(["PHONE", "EMAIL", "MEETING"]),
        priority: z.enum(["LOW", "NORMAL", "HIGH"]).default("NORMAL"),
        date: z.string(), // Keep date as we'll need it for both start and stop
        startTime: z.string().optional(),
        stopTime: z.string().optional(),
        note: z.string().optional(),
        subject: z.string().min(1, { message: t("tasks.validation.subjectRequired") }),
        subsubject: z.string().optional(),
        customer: z.string().optional(),
        seller: z.string().min(1, { message: t("tasks.validation.sellerRequired") }),
        status: z.string().optional(),
        productIds: z.array(z.string()).default([]),
    })
    .refine(
        (data) => {
            // If start time is provided, stop time must also be provided
            console.log("Validating times", data.startTime, data.stopTime);
            if (data.startTime && !data.stopTime) {
                return false;
            }
            return true;
        },
        {
            message: t("tasks.validation.bothTimesRequired"),
            path: ["stopTime"],
        },
    )
    .refine(
        (data) => {
            // If stop time is provided, start time must also be provided
            if (!data.startTime && data.stopTime) {
                return false;
            }
            return true;
        },
        {
            message: t("tasks.validation.bothTimesRequired"),
            path: ["startTime"],
        },
    )
    .refine((data) => validateWorkTime(data.startTime), {
        message: t("tasks.validation.timeOutsideBusinessHours"),
        path: ["startTime"],
    })
    .refine((data) => validateWorkTime(data.stopTime), {
        message: t("tasks.validation.timeOutsideBusinessHours"),
        path: ["stopTime"],
    })
    .refine(
        (data) => {
            if (data.startTime && data.stopTime) {
                return data.stopTime > data.startTime;
            }
            return true;
        },
        {
            message: t("tasks.validation.stopAfterStart"),
            path: ["stopTime"],
        },
    );

export function CreateTaskDialog({ onClose, onSubmitCallback, defaultSellerId, customerId }) {
    // ToDo: Refactor this so we can reuse components in Create Edit and Finalize
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const { openDialog: openCustomerSearchDialog, closeDialog: closeCustomerSearchDialog } =
        useDialogBetter();
    const [isNameValueSet, setIsNameValueSet] = useState(false);
    const [addToCalendar, setAddToCalendar] = useState(false);
    const [isAllDay, setIsAllDay] = useState(false);
    const [isTodo, setIsTodo] = useState(false);

    const currentUserId = useUserId();

    // Set dayjs locale to Polish
    dayjs.locale("pl");

    // Fetch customer by ID if customerId is provided
    const { data: customerById } = useCustomerById(customerId || "", {
        enabled: !!customerId,
    });

    // Set the fetched customer when data is available
    useEffect(() => {
        if (customerById && customerId) {
            setSelectedCustomer(customerById);
        }
    }, [customerById, customerId]);

    const today = new Date();
    const defaultTaskPeriod = getDefaultTaskPeriodStr();
    const formattedDate = new Date(
        today.getTime() - today.getTimezoneOffset() * MINUTES_TO_MILLISECONDS,
    )
        .toISOString()
        .split("T")[0];

    const { data: sellers } = useGetSellers();

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(createTaskFormSchema),
        defaultValues: {
            status: "",
            way: "PHONE",
            priority: "NORMAL",
            date: formattedDate,
            seller: "", // Will be set in useEffect after sellers are loaded
            productIds: [],
            startTime: defaultTaskPeriod.start,
            stopTime: defaultTaskPeriod.end,
        },
    });

    // Set initial seller in the form after the sellers list is fetched
    useEffect(() => {
        if (!sellers || sellers.length === 0 || !currentUserId) {
            return;
        }

        const sellerWatch = watch("seller");
        if (sellerWatch) {
            return; // Seller already set, do not override
        }

        const initialSellerId =
            defaultSellerId == null
                ? sellers.find((seller) => seller.user_id === currentUserId)?.id
                : sellers.find(
                      (seller) =>
                          seller.user_id === defaultSellerId || seller.id === defaultSellerId,
                  )?.id;

        if (!initialSellerId) {
            return; // This should never happen
        }

        setValue("seller", initialSellerId);
    }, [sellers, defaultSellerId, currentUserId]);

    const { data: statuses } = useGetTaskStatuses({
        select: (data) =>
            data.results.map((status) => ({
                label: t(`tasks.statuses.${status.status_name_eng}`),
                value: status.id,
            })),
    });

    useEffect(() => {
        if (statuses && statuses.length > 1 && !watch("status")) {
            setValue("status", statuses[1].value);
        }
    }, [statuses, setValue, watch]);

    const selectedSubject = watch("subject");
    const { data: subjects } = useGetTaskSubjects({
        select: (data) =>
            data.results.map((subject) => ({
                label: t(`tasks.subjects.${subject.name_eng}`),
                value: subject.id,
            })),
    });

    const { data: subsubjects } = useGetTaskSubsubjects(selectedSubject, {
        select: (data) =>
            data.map((subsubject) => ({
                label: t(`tasks.subsubjects.${subsubject.name_eng}`),
                value: subsubject.id,
            })),
        enabled: !!selectedSubject,
    });

    const { mutate: createTask } = useCreateTaskMutation({
        onSuccess: () => {
            toast.success(t("tasks.alerts.createSuccess"));
            onClose();
        },
        onError: (error) => {
            console.error(error);
            toast.custom(() => <SnackbarError message={t("tasks.createError")} />);
        },
    });

    const onSubmit = (data) => {
        let startDate = null;
        let stopDate = null;

        if (!isTodo && data.date) {
            if (isAllDay) {
                startDate = dayjs(data.date).startOf("day").toDate().toISOString();
                stopDate = dayjs(data.date).endOf("day").toDate().toISOString();
            } else if (data.startTime && data.stopTime) {
                const startDateTime = dayjs(`${data.date}T${data.startTime}`);
                const stopDateTime = dayjs(`${data.date}T${data.stopTime}`);
                startDate = startDateTime.toDate().toISOString();
                stopDate = stopDateTime.toDate().toISOString();
            }
        }

        const payload = {
            name: data.name,
            way: data.way,
            priority: data.priority,
            start: startDate,
            stop: stopDate,
            note: data.note,
            subject: data.subject,
            customer: selectedCustomer?.id,
            subsubject: data.subsubject || undefined,
            seller: data.seller,
            status: data.status,
            is_allday: isAllDay,
        };

        createTask(payload);

        if (addToCalendar) {
            const calendarUrl = generateOutlookCalendarLink(data, {
                customer: selectedCustomer?.person ? selectedCustomer.person : undefined,
                subjects,
                isAllDay: isAllDay,
                subsubjects,
                getTranslation: t,
            });
            window.open(calendarUrl, "_blank");
        }
        if (onSubmitCallback) {
            onSubmitCallback();
        }
    };

    const handleSelectCustomer = (customer) => {
        setSelectedCustomer(customer);
        closeCustomerSearchDialog();
    };

    const enabledBtn = watch(["name", "subject"]).every(Boolean);

    // Add a useEffect to watch for subsubject changes and update the name field
    const subsubjectId = watch("subsubject");
    useEffect(() => {
        if (!selectedSubject) {
            return;
        }

        // Check if name is empty
        // const currentName = watch("name");
        // if (currentName && currentName.trim() !== "") {
        //     return;
        // }

        // Find the selected subject and subsubject objects
        const selectedSubjectObj = subjects?.find((s) => s.value === selectedSubject);
        const selectedSubsubjectObj = subsubjects?.find((ss) => ss.value === subsubjectId);

        // If we select subject, construct the name
        if (selectedSubjectObj) {
            let generatedName = `${selectedSubjectObj.label}`
            if (selectedSubsubjectObj) {
                generatedName += ` - ${selectedSubsubjectObj.label}`
            };
            setValue("name", generatedName);
            setIsNameValueSet(true);
        }
    }, [subsubjectId, selectedSubject, setValue, subjects, subsubjects, watch]);

    useEffect(() => {
        if (isTodo && sellers && currentUserId) {
            const currentUserSeller = sellers.find((seller) => seller.user_id === currentUserId);
            if (currentUserSeller) {
                setValue("seller", currentUserSeller.id);
            }
        }
    }, [isTodo, sellers, currentUserId, setValue]);

    const currentTaskWay = watch('way')
    const currentTaskStart = watch('startTime')
    useEffect(() => {
        if (currentTaskWay && currentTaskStart) {
            console.log(currentTaskStart, "currentTaskStart")
            setValue("stopTime", getDefaultTaskPeriodStr(dayjs(`2000-01-01T${currentTaskStart}`).toDate(), watch('way')).end)
        }
    }, [currentTaskWay, setValue]);

    return (
        <BasicDialog
            open={true}
            onClose={onClose}
            titleAlign="center"
            contentAlign="center"
            title={t("tasks.addTaskDialog.title")}
            maxWidth="md"
            showDialogActions={false}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                    {/* Customer Selection */}
                    <Grid item xs={12}>
                        <Typography sx={{ mb: 2, fontSize: 18 }}>
                            {t("tasks.addTaskDialog.customerSection")}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                            {!selectedCustomer ? (
                                <Stack direction="row" justifyContent="center" width="100%">
                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            openCustomerSearchDialog({
                                                Component: SearchOtherClientDialog,
                                                componentProps: {
                                                    title: t("tasks.addTaskDialog.selectCustomer"),
                                                    onCustomerSelected: handleSelectCustomer,
                                                    includeCompany: true,
                                                },
                                                onCloseFn: closeCustomerSearchDialog,
                                            })
                                        }
                                        endIcon={<SearchIcon />}
                                    >
                                        {t("tasks.addTaskDialog.selectCustomerBtn")}
                                    </Button>
                                </Stack>
                            ) : (
                                <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                    <IconButton
                                        color="primary"
                                        onClick={() =>
                                            openCustomerSearchDialog({
                                                Component: SearchOtherClientDialog,
                                                componentProps: {
                                                    title: t("tasks.addTaskDialog.selectCustomer"),
                                                    onCustomerSelected: handleSelectCustomer,
                                                    includeCompany: true,
                                                },
                                                onCloseFn: closeCustomerSearchDialog,
                                            })
                                        }
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                    <TextField
                                        value={
                                            selectedCustomer.company
                                                ? selectedCustomer.company?.name_short
                                                : selectedCustomer.person?.name
                                        }
                                        InputProps={{ readOnly: true }}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                    />
                                    <TextField
                                        value={
                                            selectedCustomer.company
                                                ? selectedCustomer.company?.nip
                                                : selectedCustomer.person?.surname
                                        }
                                        InputProps={{ readOnly: true }}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                    />
                                    <ClientPassportButton
                                        clientId={selectedCustomer.id}
                                        clientType={
                                            selectedCustomer.company ? "company" : "customer"
                                        }
                                    />
                                    <IconButton
                                        color="error"
                                        onClick={() => setSelectedCustomer(null)}
                                    >
                                        {/* X icon */}
                                        {/* biome-ignore lint/a11y/noSvgWithoutTitle: <legacy> */}
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path
                                                d="M18 6L6 18"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                            />
                                            <path
                                                d="M6 6L18 18"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </IconButton>
                                </Stack>
                            )}
                        </Stack>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    {/* Task Details */}
                    <Grid item xs={12}>
                        <Typography sx={{ fontSize: 18 }}>
                            {t("tasks.addTaskDialog.taskDetails")}
                        </Typography>
                    </Grid>

                    {/* Task Name */}
                    <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name="name"
                            label={`${t("tasks.addTaskDialog.name")}*`}
                            props={{
                                error: !!errors.name,
                                helperText: errors.name?.message,
                                InputLabelProps: {
                                    shrink: isNameValueSet || !!watch("name"),
                                },
                            }}
                        />
                    </Grid>

                    {/* Subject and Subsubject */}
                    <Grid item xs={6}>
                        <ControlledSelect
                            control={control}
                            name="subject"
                            label={`${t("tasks.addTaskDialog.subject")}*`}
                            data={subjects || []}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <ControlledSelect
                            control={control}
                            name="subsubject"
                            label={t("tasks.addTaskDialog.subsubject")}
                            data={subsubjects || []}
                            disabled={!selectedSubject || subsubjects?.length === 0}
                        />
                    </Grid>

                    {/* All Day / TODO Checkboxes */}
                    <AllDayTodoSwitches
                        isAllDay={isAllDay}
                        setIsAllDay={setIsAllDay}
                        isTodo={isTodo}
                        setIsTodo={setIsTodo}
                        setValue={setValue}
                        t={t}
                    />

                    {/* Date and Times */}
                    <Grid item xs={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
                            <Controller
                                control={control}
                                name="date"
                                render={({ field }) => (
                                    <DatePicker
                                        label={`${t("tasks.addTaskDialog.date")}`}
                                        value={field.value ? dayjs(field.value) : null}
                                        onChange={(date) => {
                                            field.onChange(date ? date.format("YYYY-MM-DD") : "");
                                        }}
                                        disabled={isTodo}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                error={!!errors.date}
                                                helperText={errors.date?.message}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
                            <Controller
                                control={control}
                                name="startTime"
                                render={({ field }) => (
                                    <TimePicker
                                        label={t("tasks.addTaskDialog.startTime")}
                                        value={
                                            field.value ? dayjs(`2000-01-01T${field.value}`) : null
                                        }
                                        minTime={MIN_WORK_TIME_DAYJS}
                                        maxTime={MAX_WORK_TIME_DAYJS}
                                        onChange={(time) => {
                                            const timeString = time?.format("HH:mm") || "";
                                            field.onChange(timeString);
                                            // update task end time base on taskWay and startTime (from dayJs to navite date())
                                            setValue("stopTime", getDefaultTaskPeriodStr(time.toDate(), watch('way')).end);
                                        }}
                                        disabled={isAllDay || isTodo}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                error={!!errors.startTime}
                                                helperText={errors.startTime?.message}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
                            <Controller
                                control={control}
                                name="stopTime"
                                render={({ field }) => (
                                    <TimePicker
                                        label={t("tasks.addTaskDialog.stopTime")}
                                        value={
                                            field.value ? dayjs(`2000-01-01T${field.value}`) : null
                                        }
                                        minTime={
                                            watch("startTime")
                                                ? dayjs(`2000-01-01T${watch("startTime")}`)
                                                : MIN_WORK_TIME_DAYJS
                                        }
                                        maxTime={MAX_WORK_TIME_DAYJS}
                                        onChange={(time) => {
                                            const timeString = time?.format("HH:mm") || "";
                                            field.onChange(timeString);
                                        }}
                                        disabled={isAllDay || isTodo}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                error={!!errors.stopTime}
                                                helperText={errors.stopTime?.message}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>

                    {/* Status */}
                    <Grid item xs={4}>
                        <ControlledSelect
                            control={control}
                            name="status"
                            label={t("tasks.addTaskDialog.status")}
                            data={statuses || []}
                        />
                    </Grid>

                    {/* Way */}
                    <Grid item xs={4}>
                        <ControlledSelect
                            control={control}
                            name="way"
                            label={`${t("tasks.addTaskDialog.way")}*`}
                            data={[
                                { value: "PHONE", label: t("tasks.ways.PHONE") },
                                { value: "EMAIL", label: t("tasks.ways.EMAIL") },
                                { value: "MEETING", label: t("tasks.ways.MEETING") },
                            ]}
                        />
                    </Grid>

                    {/* Priority */}
                    <Grid item xs={4}>
                        <ControlledSelect
                            control={control}
                            name="priority"
                            label={`${t("tasks.addTaskDialog.priority")}*`}
                            data={[
                                { value: "LOW", label: t("tasks.priorities.LOW") },
                                { value: "NORMAL", label: t("tasks.priorities.NORMAL") },
                                { value: "HIGH", label: t("tasks.priorities.HIGH") },
                            ]}
                        />
                    </Grid>

                    {/* Seller */}
                    <Grid item xs={12}>
                        <ControlledSelect
                            control={control}
                            disabled={isTodo}
                            name="seller"
                            label={`${t("tasks.addTaskDialog.seller")}*`}
                            data={
                                sellers?.map((seller) => ({
                                    value: seller.id,
                                    label: seller.full_name,
                                })) || []
                            }
                        />
                    </Grid>

                    {/* Note */}
                    <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name="note"
                            label={`${t("tasks.addTaskDialog.note")}`}
                            props={{
                                InputProps: {
                                    rows: 3,
                                    multiline: true,
                                },
                            }}
                        />
                    </Grid>

                    {/* Buttons */}
                    <Grid
                        item
                        xs={12}
                        container
                        justifyContent="center"
                        spacing={2}
                        alignItems="center"
                    >
                        <Grid item xs>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                justifyContent="flex-start"
                            >
                                <Checkbox
                                    checked={addToCalendar}
                                    onChange={(e) => setAddToCalendar(e.target.checked)}
                                />
                                <CalendarTodayIcon color={addToCalendar ? "primary" : "action"} />
                            </Stack>
                        </Grid>
                        <Grid item>
                            <Button type="button" variant="outlined" onClick={onClose}>
                                {t("tasks.addTaskDialog.cancel")}
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button type="submit" variant="contained" disabled={!enabledBtn}>
                                {t("tasks.addTaskDialog.create")}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </form>
        </BasicDialog>
    );
}
